import api from './axios';
import type { Product } from '../types/product';

/** Aligned with admin GET /api/shop/new-products/zone (minimal fields). */
export interface NewProductsZoneCategoryMeta {
  id: number;
  name: string;
  nameZh: string | null;
  nameCn: string | null;
  imageUrl: string | null;
}

export interface NewProductsZoneProductPayload {
  id: number;
  name: string;
  nameZh: string | null;
  nameCn: string | null;
  price: number;
  comparePrice: number | null;
  imageUrl: string | null;
  brandId: number | null;
  brandInfo: {
    id: number;
    name: string;
    nameZh: string | null;
    nameCn: string | null;
  } | null;
}

export interface NewProductsZoneCategoryPayload {
  category: NewProductsZoneCategoryMeta;
  products: NewProductsZoneProductPayload[];
}

export interface NewProductsZonePayload {
  categories: NewProductsZoneCategoryPayload[];
}

export interface NewProductsZoneParams {
  categoryCap?: number;
  minProducts?: number;
  productsPerCategory?: number;
}

export interface NewProductCategory {
  id: number;
  name: string;
  nameZh: string;
  nameCn: string;
  description: string;
  descriptionZh: string;
  descriptionCn: string;
  isActive: boolean;
  parentId: number | null;
  createdAt: string;
  imageUrl: string | null;
}

export interface NewProductsResponse {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface NewProductsParams {
  page: number;
  limit: number;
  sortBy: 'new' | 'popular' | 'price_low' | 'price_high';
  categoryId?: number;
}

export const newProductsApi = {
  getNewProductsZone: async (params?: NewProductsZoneParams): Promise<NewProductsZonePayload> => {
    const searchParams = new URLSearchParams();
    if (params?.categoryCap != null) searchParams.set('categoryCap', String(params.categoryCap));
    if (params?.minProducts != null) searchParams.set('minProducts', String(params.minProducts));
    if (params?.productsPerCategory != null) {
      searchParams.set('productsPerCategory', String(params.productsPerCategory));
    }
    const qs = searchParams.toString();
    const response = await api.get<NewProductsZonePayload>(
      `/api/shop/new-products/zone${qs ? `?${qs}` : ''}`
    );
    return response.data;
  },

  // Get new product categories
  getNewProductCategories: async (): Promise<NewProductCategory[]> => {
    try {
      const response = await api.get('/api/shop/new-products/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching new product categories:', error);
      return [];
    }
  },

  // Get new products with optional category filter
  getNewProducts: async (params: NewProductsParams): Promise<NewProductsResponse> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        limit: params.limit.toString(),
        sortBy: params.sortBy,
        ...(params.categoryId && { categoryId: params.categoryId.toString() }),
      });

      const response = await api.get(`/api/shop/new-products?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching new products:', error);
      throw error;
    }
    },
};
